import {
  ParametresTraverses,
  StructureTraverses,
  TraversesAPIResponse,
  TypeMotifTraverse,
  MotifPersonnalise,
  DonneesStructureBase
} from '../types';

/**
 * API pour la gestion des traverses
 * Gère la communication avec le backend pour la génération et validation des traverses
 */
export class TraversesAPI {
  private baseURL: string;
  private timeout: number;

  constructor(baseURL: string = '', timeout: number = 10000) {
    this.baseURL = baseURL;
    this.timeout = timeout;
  }

  /**
   * Génère la structure des traverses
   */
  public async genererTraverses(
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): Promise<TraversesAPIResponse> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.baseURL}/api/traverses/generer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parametres,
          donneesBase
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status} - ${response.statusText}`);
      }

      const data: TraversesAPIResponse = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erreur inconnue lors de la génération des traverses');
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: 'Timeout: La génération des traverses a pris trop de temps',
            timestamp: Date.now()
          };
        }
        return {
          success: false,
          error: `Erreur lors de la génération des traverses: ${error.message}`,
          timestamp: Date.now()
        };
      }
      return {
        success: false,
        error: 'Erreur inconnue lors de la génération des traverses',
        timestamp: Date.now()
      };
    }
  }

  /**
   * Valide les paramètres des traverses
   */
  public async validerParametres(
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): Promise<{
    valide: boolean;
    erreurs: string[];
    avertissements: string[];
  }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.baseURL}/api/traverses/valider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parametres,
          donneesBase
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return {
        valide: false,
        erreurs: ['Erreur de communication avec le serveur'],
        avertissements: []
      };
    }
  }



  /**
   * Sauvegarde un motif personnalisé
   */
  public async sauvegarderMotifPersonnalise(
    motif: MotifPersonnalise
  ): Promise<{ success: boolean; error?: string; motif?: MotifPersonnalise }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.baseURL}/api/traverses/motifs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(motif),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Récupère la liste des motifs personnalisés sauvegardés
   */
  public async obtenirMotifsPersonnalises(): Promise<MotifPersonnalise[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.baseURL}/api/traverses/motifs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data.motifs || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Supprime un motif personnalisé
   */
  public async supprimerMotifPersonnalise(
    motifId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.baseURL}/api/traverses/motifs/${motifId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Teste la connectivité avec le serveur
   */
  public async testerConnectivite(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.baseURL}/api/health`, {
        method: 'GET',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      return false;
    }
  }





  /**
   * Met à jour l'URL de base
   */
  public mettreAJourBaseURL(nouvelleURL: string): void {
    this.baseURL = nouvelleURL;
  }

  /**
   * Met à jour le timeout
   */
  public mettreAJourTimeout(nouveauTimeout: number): void {
    this.timeout = Math.max(1000, nouveauTimeout);
  }
}
